import { defineStore } from 'pinia'
import { computed, reactive } from 'vue'
import {useUserStore} from './user.ts'

/**
 * Structure of settings in localStorage:
 * _settings: {
 *   [formId]: {
 *     timestamp: number,
 *     pages: {                    // lvl page
 *       [pageId]: {
 *         panels: {               // lvl panel
 *           [panelId]: {
 *             panelOpen: boolean,
 *             elements: {         // lvl element
 *               [elementId]: {
 *                 [settingKey]: unknown
 *               }
 *             }
 *           }
 *         }
 *       }
 *     }
 *   }
 * }
 */

export const STORAGE_SETTINGS_KEY = '_settings';

// Setting levels
export const SETTING_LEVELS = {
  PAGE: 'pages',
  PANEL: 'panels',
  ELEMENT: 'elements'
} as const

// Type for setting levels
export type SettingLevel = typeof SETTING_LEVELS[keyof typeof SETTING_LEVELS]

// Path to setting
export interface SettingPath {
  formId: string | number
  pageId?: string | number
  panelId?: string | number
  elementId?: string | number
}

// Parameters for set setting
export interface SetSettingParams {
  level: SettingLevel
  path: SettingPath
  key: string
  value: unknown
}

// Parameters for getting a setting
export interface GetSettingParams {
  level: SettingLevel
  path: SettingPath
  key: string
}

// Element settings parameters
export interface ElementSettingsParams {
  itemsPerPage?: number
  selectedColumns?: string[]
  /** Пользовательские ширины колонок таблицы (px), ключ — field name */
  columnWidths?: Record<string, number>
  /** TreeTable showFormStateOptions — развёрнутые ключи */
  expandedKeys?: Record<string, boolean>
}
// Element settings type
type ElementSettings = Record<string, unknown>

// Panel settings type (keys)
export interface PanelSettingsParams {
  panelOpen?: boolean
}
interface PanelSettings extends PanelSettingsParams {
  elements?: Record<string | number, ElementSettings>
}

// Structure of page settings
interface PageSettings {
  panels?: Record<string | number, PanelSettings>
}

// Structure of form settings
interface FormSettings {
  timestamp?: number
  pages: Record<string | number, PageSettings>
}

// Settings store type
type SettingsStore = Record<string | number, FormSettings>

export const useSettingsStore = defineStore('settings', () =>{
  let settings = reactive<SettingsStore>({})
  const userStore = useUserStore()

  const userStorageKey = computed(() => (userStore?.user?.username ?? 'user') + STORAGE_SETTINGS_KEY)

  const removeOldest = (settingsData: SettingsStore) => {
    const settingsKeys = Object.keys(settingsData)
    if (settingsKeys.length === 0) return settingsData
    let oldestKey: string | null = null
    let minTimestamp = 0

    for (const key of settingsKeys) {
      const timestamp = settingsData[key]?.timestamp ?? 0
      if (timestamp < minTimestamp) {
        minTimestamp = timestamp
        oldestKey = key
      }
    }

    if (oldestKey !== null) {
      delete settingsData[oldestKey]
    }
    return settingsData
  }

  const saveToLocalStorage = () => {
    const dataToSave = JSON.stringify(settings)
    try {
      localStorage.setItem(userStorageKey.value, dataToSave)
    } catch (error) {
      if (error instanceof DOMException && (error.name === 'QuotaExceededError')) {
        console.error('QuotaExceededError')
        settings = removeOldest(settings)
        try {
          const retryData = JSON.stringify(settings)
          localStorage.setItem(userStorageKey.value, retryData)
        } catch(retryError) {
          console.error('Failed to save: ', retryError)
          removeSettingsFromStore()
        }
      } else {
        console.error('localStorage error:', error)
      }
    }
  }

  /**
   * Set setting function
   * @param params - Setting parameters
   * @param params.level - Setting level (page, panel, element)
   * @param params.path - Path to setting (formId, pageId, panelId, elementId)
   * @param params.key - Key
   * @param params.value - Value
   */
  const setSetting = (params: SetSettingParams) => {
    const { level, path, key, value } = params
    const { formId, pageId, panelId, elementId } = path

    if (!settings[formId]) {
      settings[formId] = { timestamp: Date.now(), pages: {} }
    }

    const pathSegments: (string | number)[] = [SETTING_LEVELS.PAGE]

    if (pageId !== undefined) {
      pathSegments.push(pageId)
    }

    if (level === SETTING_LEVELS.PANEL || level === SETTING_LEVELS.ELEMENT) {
      pathSegments.push(SETTING_LEVELS.PANEL)
      if (panelId !== undefined) {
        pathSegments.push(panelId)
      }
    }

    if (level === SETTING_LEVELS.ELEMENT) {
      pathSegments.push(SETTING_LEVELS.ELEMENT)
      if (elementId !== undefined) {
        pathSegments.push(elementId)
      }
    }


    let current: any = settings[formId]
    for (const segment of pathSegments) {
      if (!current[segment]) {
        current[segment] = {}
      }
      current = current[segment]
    }


    current[key] = value

    settings[formId].timestamp = Date.now()

    saveToLocalStorage()
  }

  /**
   * Get setting function
   * @param params - Setting parameters
   * @param params.level - Setting level (page, panel, element)
   * @param params.path - Path to setting (formId, pageId, panelId, elementId)
   * @param params.key - Key
   * @returns Value of setting or undefined
   */
  const getSetting = <ReturnValueType>(params: GetSettingParams): ReturnValueType | undefined => {

    const { level, path, key } = params
    const { formId, pageId, panelId, elementId } = path

    const pathSegments: (string | number)[] = [SETTING_LEVELS.PAGE]

    if (pageId !== undefined) {
      pathSegments.push(pageId)
    }

    if (level === SETTING_LEVELS.PANEL || level === SETTING_LEVELS.ELEMENT) {
      pathSegments.push(SETTING_LEVELS.PANEL)
      if (panelId !== undefined) {
        pathSegments.push(panelId)
      }
    }

    if (level === SETTING_LEVELS.ELEMENT) {
      pathSegments.push(SETTING_LEVELS.ELEMENT)
      if (elementId !== undefined) {
        pathSegments.push(elementId)
      }
    }

    let current: any = settings[formId]
    for (const segment of pathSegments) {
      if (!current?.[segment]) {
        return undefined
      }
      current = current[segment]
    }

    return current?.[key]
  }

  /**
   * Save panel setting
   * @param formId - ID form
   * @param pageId - ID page
   * @param panelId - ID panel
   * @param key - Key
   * @param value - Value
   */
  const setPanelSetting = <KeyType extends keyof PanelSettingsParams>(
    formId: string | number,
    pageId: string | number,
    panelId: string | number,
    key: KeyType,
    value: PanelSettingsParams[KeyType]
  ) => {
    setSetting({
      level: SETTING_LEVELS.PANEL,
      path: { formId, pageId, panelId },
      key,
      value
    })
  }

  /**
   * Get setting panel
   * @param formId - ID form
   * @param pageId - ID page
   * @param panelId - ID panel
   * @param key - Key
   */
  const getPanelSetting = (
    formId: string | number,
    pageId: string | number,
    panelId: string | number,
    key: keyof PanelSettingsParams,
  ): PanelSettingsParams[keyof PanelSettingsParams] => {
    return getSetting<PanelSettingsParams[keyof PanelSettingsParams]>({
      level: SETTING_LEVELS.PANEL,
      path: { formId, pageId, panelId },
      key
    })
  }

  /**
   * Set element setting
   * @param formId - ID form
   * @param pageId - ID page
   * @param panelId - ID panel
   * @param elementId - ID element
   * @param key - Key setting
   * @param value - Value setting
   */
  const setElementSetting = <KeyType extends keyof ElementSettingsParams>(
    formId: string | number,
    pageId: string | number,
    panelId: string | number,
    elementId: string | number,
    key: KeyType,
    value: ElementSettingsParams[KeyType]
  ) => {
    setSetting({
      level: SETTING_LEVELS.ELEMENT,
      path: { formId, pageId, panelId, elementId },
      key,
      value
    })
  }

  /**
   * Getting element setting
   * @param formId - ID form
   * @param pageId - ID page
   * @param panelId - ID panel
   * @param elementId - ID element
   * @param key - Key
   */
  const getElementSetting = (
    formId: string | number,
    pageId: string | number,
    panelId: string | number,
    elementId: string | number,
    key: keyof ElementSettingsParams,
  ): ElementSettingsParams[keyof ElementSettingsParams] => {
    return getSetting<ElementSettingsParams[keyof ElementSettingsParams]>({
      level: SETTING_LEVELS.ELEMENT,
      path: { formId, pageId, panelId, elementId },
      key
    })
  }

  const getSettingsFromStore = (): SettingsStore => {
    const storageData = localStorage.getItem(userStorageKey.value);
    if (storageData) {
      try {
        const parsed = JSON.parse(storageData);
        if (typeof parsed === 'object') {
          return parsed
        }
      } catch (e) {
        console.error('Ошибка парсинга настроек из localStorage:', e);
        localStorage.removeItem(userStorageKey.value);
      }
    }
    return settings;
  }

  const removeSettingsFromStore = () => {
    settings = {}
    localStorage.removeItem(userStorageKey.value);
  }

  const setSettingsSync = () => {
    const loaded = getSettingsFromStore()

    Object.keys(settings).forEach(key => {
      delete settings[key]
    })

    Object.keys(loaded).forEach(key => {
      settings[key] = loaded[key]
    })
  }

  return {
    setSetting,
    getSetting,

    setPanelSetting,
    getPanelSetting,
    setElementSetting,
    getElementSetting,

    getSettingsFromStore,
    removeSettingsFromStore,
    setSettingsSync
  }
})
